/**
 * The skyAgent / skyBrain surface catalog — the single declarative source for
 * both sections' navigation.
 *
 * This is skyCommand's app-surface spine
 * (`SkyCommand-live/shared/config/spine/appSurface.ts`) minus its override
 * layers. skyCommand folds three layers — a global catalog, a vertical pack
 * manifest and per-instance `instance_config_overrides` rows — because a
 * customer there can hide or reorder a nav entry. SkyHire has no equivalent
 * store and no editor over one, so a surface is either in this list or it does
 * not exist; adding the layers would be building a configuration system nobody
 * has asked for.
 *
 * Everything downstream reads this file and nothing hardcodes a copy:
 *   - `sidebarConfig.ts`   builds both sidebar sections from it
 *   - `routeMapping.ts`    maps nav id to route, and back for highlighting
 *   - each page            takes its header title and description from it
 *
 * Access is deliberately NOT modelled here. Every one of these surfaces sits
 * behind the same gate the section has always had — `isAdmin` plus the
 * instance's SkyAgent entitlement (see `skyAgentGating.ts`) — so there is no
 * per-surface permission to carry. skyCommand's catalog has one per item
 * because it split `manage_skyagent` into four keys; SkyHire has not.
 */
import {
  Brain,
  CalendarClock,
  ClipboardCheck,
  Cog,
  LayoutDashboard,
  Lightbulb,
  MessageCircle,
  Sparkles,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { ROUTES } from "../../components/shared/constant";

/**
 * The two top-level sidebar sections. Also `SkySectionShellProps["product"]`.
 * A sidebar SECTION, not a sellable product; products are `shared/products/keys.ts`.
 */
export type SkySurfaceProduct = "skyagent" | "skybrain";

/**
 * Sidebar section headers.
 *
 * Lower-case "sky" with a capitalised second word is the brand's own casing,
 * the same treatment the wordmarks use. The old section header was "SkyAgent",
 * which matched neither.
 */
export const SKY_SURFACE_LABELS: Record<SkySurfaceProduct, string> = {
  skyagent: "skyAgent",
  skybrain: "skyBrain",
};

/**
 * Nav ids for every surface.
 *
 * Spread into `NAV_IDS` in sidebarConfig.ts rather than duplicated there, so
 * `routeMapping.ts`'s `Record<NavId, …>` maps fail to compile the moment a
 * surface is added here without being routed. That is the whole reason these
 * are ids and not just array indexes.
 */
export const SKY_NAV_IDS = {
  SKYAGENT_WORKSPACE: "skyagent-workspace",
  SKYAGENT_AI_EMPLOYEES: "skyagent-ai-employees",
  SKYAGENT_APPROVALS: "skyagent-approvals",
  SKYAGENT_ARTIFACTS: "skyagent-artifacts",
  SKYAGENT_CONFIGURATION: "skyagent-configuration",
  SKYBRAIN_OVERVIEW: "skybrain-overview",
  SKYBRAIN_MEMORY: "skybrain-memory",
  SKYBRAIN_SKILLS: "skybrain-skills",
  SKYBRAIN_QUICK_PROMPTS: "skybrain-quick-prompts",
} as const;

export type SkyNavId = (typeof SKY_NAV_IDS)[keyof typeof SKY_NAV_IDS];

export interface SkySurface {
  /** Stable key, used to look a surface up from a page. */
  key: SkySurfaceKey;
  /** Which of the two sidebar sections this surface belongs to. */
  product: SkySurfaceProduct;
  /** Sidebar label, and the page title after the lockup's divider. */
  label: string;
  /** The one-line description under the page header. */
  description: string;
  /** Sidebar icon. lucide, matching every other section in this sidebar. */
  icon: LucideIcon;
  /** Route base, from `ROUTES`. */
  href: string;
  /** Sidebar nav id. */
  navId: SkyNavId;
  /** Position within its section. */
  order: number;
  /**
   * Routable, but no sidebar row.
   *
   * The route still has to be reachable, so `routeMapping.ts` hangs it off the
   * nav row that links to it — AppLayout builds the route guard's allow-list
   * from sidebar entries alone, and a surface with no entry anywhere is bounced
   * to Applicants.
   */
  hiddenFromNav?: boolean;
  /**
   * Retired URLs that now redirect here.
   *
   * They are listed on the surface rather than in the redirect table alone
   * because `routeMapping.ts` also feeds AppLayout's route guard: a path with
   * no nav entry behind it is bounced to the first allowed page before its
   * redirect ever renders, so an old bookmark would land on Applicants instead
   * of the page that replaced it.
   */
  legacyHrefs?: readonly string[];
}

export type SkySurfaceKey =
  | "workspace"
  | "ai-employees"
  | "approvals"
  | "artifacts"
  | "configuration"
  | "overview"
  | "memory"
  | "skills"
  | "quick-prompts";

/**
 * The catalog. Order within each product is the sidebar order.
 *
 * Descriptions are written to say what the surface is FOR, not to restate its
 * title — they are the page's only subheading, and a description that repeats
 * the title is the clutter rule's exact example.
 */
export const SKY_SURFACES: readonly SkySurface[] = [
  // ── skyAgent — doing the work ──────────────────────────────────────────
  {
    key: "workspace",
    product: "skyagent",
    label: "Workspace",
    description: "Chat with skyAgent and pick up where a previous session left off.",
    icon: MessageCircle,
    href: ROUTES.SKYAGENT,
    navId: SKY_NAV_IDS.SKYAGENT_WORKSPACE,
    order: 0,
    // skyBrain's Session History was a read-only copy of the conversation list
    // this page already shows, where the same chats can be searched, reopened
    // and continued. It was removed (as in skySchedule) and its URL now lands here.
    legacyHrefs: [ROUTES.SKYBRAIN_SESSION_HISTORY],
  },
  {
    key: "ai-employees",
    product: "skyagent",
    label: "AI Employees",
    description: "Standing assignments that run on a schedule and report back by email.",
    icon: CalendarClock,
    href: ROUTES.SKYAGENT_AI_EMPLOYEES,
    navId: SKY_NAV_IDS.SKYAGENT_AI_EMPLOYEES,
    order: 1,
  },
  {
    key: "approvals",
    product: "skyagent",
    label: "Approvals",
    // "answered by the person whose session raised it", not "waiting for
    // someone to approve": approve and reject 403 for anyone but the
    // session owner, so the queue is instance-wide to READ and owner-only
    // to act on. The old wording implied whoever opened the page could
    // clear it.
    description:
      "Actions skyAgent paused for a decision, each answered by whoever's session raised it.",
    icon: ClipboardCheck,
    href: ROUTES.SKYAGENT_APPROVALS,
    navId: SKY_NAV_IDS.SKYAGENT_APPROVALS,
    order: 2,
  },
  {
    key: "artifacts",
    product: "skyagent",
    label: "Artifacts",
    // Derived from your own answers, so the description says "your". Nothing is
    // stored when one is produced; they are regions of assistant messages.
    description: "Tables, code and write-ups skyAgent produced in your conversations.",
    icon: Sparkles,
    href: ROUTES.SKYAGENT_ARTIFACTS,
    navId: SKY_NAV_IDS.SKYAGENT_ARTIFACTS,
    order: 3,
    // Reached from the Workspace rail, not the sidebar: artifacts are what a
    // conversation produced, so they belong beside the conversation rather
    // than as a sixth row competing with it.
    hiddenFromNav: true,
  },
  {
    key: "configuration",
    product: "skyagent",
    label: "Configuration",
    // Named after what the page holds. It claimed "limits, tools and
    // access" and has never had tools or access controls on it — not
    // before the split and not after. Two descriptions in this catalog
    // described a different page from the one that shipped, so each one
    // below was re-read against its screen rather than its title.
    description:
      "The instructions this instance gives skyAgent, and how long it may work on one answer.",
    icon: Cog,
    href: ROUTES.SKYAGENT_CONFIGURATION,
    navId: SKY_NAV_IDS.SKYAGENT_CONFIGURATION,
    order: 4,
    // The old combined admin page. Its four tabs are now four surfaces across
    // both sections; Configuration was the tab it opened on.
    legacyHrefs: [ROUTES.SKYAGENT_ADMIN],
  },

  // ── skyBrain — what it knows ───────────────────────────────────────────
  {
    key: "overview",
    product: "skybrain",
    label: "Overview",
    description: "What skyAgent has built up so far, and a way into each skyBrain surface.",
    icon: LayoutDashboard,
    href: ROUTES.SKYBRAIN,
    navId: SKY_NAV_IDS.SKYBRAIN_OVERVIEW,
    order: 0,
  },
  {
    key: "memory",
    product: "skybrain",
    label: "Memory",
    description: "Long-term memories, and the journal entries they are promoted from.",
    icon: Brain,
    href: ROUTES.SKYBRAIN_MEMORY,
    navId: SKY_NAV_IDS.SKYBRAIN_MEMORY,
    order: 1,
    legacyHrefs: [ROUTES.SKYAGENT_MEMORY],
  },
  {
    key: "skills",
    product: "skybrain",
    label: "Skills",
    description: "Reusable procedures skyAgent can follow, and which of them are switched on.",
    icon: Zap,
    href: ROUTES.SKYBRAIN_SKILLS,
    navId: SKY_NAV_IDS.SKYBRAIN_SKILLS,
    order: 2,
    // Was "Capabilities", which covered skills and tools together.
    legacyHrefs: [ROUTES.SKYAGENT_CAPABILITIES],
  },
  {
    key: "quick-prompts",
    product: "skybrain",
    label: "Quick Prompts",
    description: "The starter prompts offered on an empty Workspace, and from the / menu.",
    icon: Lightbulb,
    href: ROUTES.SKYBRAIN_QUICK_PROMPTS,
    navId: SKY_NAV_IDS.SKYBRAIN_QUICK_PROMPTS,
    order: 3,
    // Routable, but not a nav row. It is reached from the Overview's Explore
    // card. The page is worth having and is not worth a permanent line in a
    // three-item section; skyCommand keeps surfaces this way too.
    hiddenFromNav: true,
  },
];

const BY_KEY = new Map<SkySurfaceKey, SkySurface>(SKY_SURFACES.map((s) => [s.key, s]));

/**
 * The surface with this key.
 *
 * Throws rather than returning undefined: every caller is a page that cannot
 * render its own header without one, and a silent undefined would surface as a
 * blank title rather than as a failure.
 */
export function skySurface(key: SkySurfaceKey): SkySurface {
  const surface = BY_KEY.get(key);
  if (!surface) throw new Error(`Unknown sky surface: ${key}`);
  return surface;
}

/** Every surface in one section, in order, nav row or not. */
export function skySurfacesFor(product: SkySurfaceProduct): SkySurface[] {
  return SKY_SURFACES.filter((s) => s.product === product).sort((a, b) => a.order - b.order);
}

/** The surfaces that get a sidebar row, in order. What the nav renders. */
export function skyNavSurfacesFor(product: SkySurfaceProduct): SkySurface[] {
  return skySurfacesFor(product).filter((s) => !s.hiddenFromNav);
}

/** A surface's own route plus any retired ones that redirect to it. */
export function skySurfaceRoutes(key: SkySurfaceKey): string[] {
  const surface = skySurface(key);
  return [surface.href, ...(surface.legacyHrefs ?? [])];
}

/**
 * Header props for a surface, spread into `SkySectionShell` by its page.
 *
 * Keeps the 11 pages from each restating a title and description that this
 * catalog already owns.
 */
export function skySurfaceHeader(key: SkySurfaceKey): {
  product: SkySurfaceProduct;
  title: string;
  description: string;
} {
  const { product, label, description } = skySurface(key);
  return { product, title: label, description };
}
